import { languageForPath } from '../util/language-for-path';
import { base64ToUtf8, utf8ToBase64 } from './base64';

const API_BASE = 'https://api.github.com';
const MAX_FILE_BYTES = 512 * 1024; // matches the local-folder loader's cap
const SKIP_DIR_SEGMENTS = new Set(['node_modules', '.git', 'dist', 'build', '.venv', '__pycache__']);

export class GitHubApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function ghFetch(token: string, path: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `token ${token}`,
      Accept: 'application/vnd.github+json',
      'Content-Type': 'application/json',
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as { message?: string });
    throw new GitHubApiError((body as { message?: string }).message ?? `GitHub API error (${res.status})`, res.status);
  }
  return res;
}

function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

export async function getDefaultBranch(token: string, owner: string, repo: string): Promise<string> {
  const res = await ghFetch(token, `/repos/${owner}/${repo}`);
  const json = (await res.json()) as { default_branch: string };
  return json.default_branch;
}

export interface GitHubFileEntry {
  path: string;
  sha: string;
  size: number;
}

/**
 * Lists every text file in the repo/branch that the app can actually parse (same language filter
 * and size cap as opening a local folder) via the recursive git trees API — one call for the
 * whole tree rather than walking directories one at a time.
 */
export async function listRepoFiles(token: string, owner: string, repo: string, branch: string): Promise<GitHubFileEntry[]> {
  const res = await ghFetch(token, `/repos/${owner}/${repo}/git/trees/${encodeURIComponent(branch)}?recursive=1`);
  const json = (await res.json()) as { tree: { path: string; type: string; sha: string; size?: number }[] };
  return json.tree
    .filter((entry) => entry.type === 'blob')
    .filter((entry) => !entry.path.split('/').some((seg) => SKIP_DIR_SEGMENTS.has(seg)))
    .filter((entry) => languageForPath(entry.path) !== undefined)
    .filter((entry) => (entry.size ?? 0) <= MAX_FILE_BYTES)
    .map((entry) => ({ path: entry.path, sha: entry.sha, size: entry.size ?? 0 }));
}

export async function getFileContent(
  token: string,
  owner: string,
  repo: string,
  path: string,
  ref: string,
): Promise<{ content: string; sha: string }> {
  const res = await ghFetch(token, `/repos/${owner}/${repo}/contents/${encodePath(path)}?ref=${encodeURIComponent(ref)}`);
  const json = (await res.json()) as { content: string; encoding: string; sha: string };
  if (json.encoding !== 'base64') throw new Error(`Unexpected encoding for ${path}: ${json.encoding}`);
  return { content: base64ToUtf8(json.content), sha: json.sha };
}

/** Commits a single-file update via the Contents API — simple and atomic per file, not a multi-file tree commit. */
export async function commitFile(
  token: string,
  owner: string,
  repo: string,
  path: string,
  content: string,
  sha: string,
  branch: string,
  message: string,
): Promise<{ sha: string }> {
  const res = await ghFetch(token, `/repos/${owner}/${repo}/contents/${encodePath(path)}`, {
    method: 'PUT',
    body: JSON.stringify({ message, content: utf8ToBase64(content), sha, branch }),
  });
  const json = (await res.json()) as { content: { sha: string } };
  return { sha: json.content.sha };
}
